import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {ObjectTable} from './ObjectTable';
import {generateHtmlId} from 'mrf/utils/StringGenerators';
import TableFooter from './TableFooter';
import {resourceGetList} from 'mrf/redux/ResourceFetcher';
import {useFormFullSelector, useFormSelector} from 'mrf/redux/ReduxHelper';
import store from 'mrf/redux/store';
import {addResourceAction} from 'mrf/redux/actions/resource';
import Flex from 'mrf/layout/Flex';
import IF from './IF';
import SearchField from './SearchField';
import Pagination from './Pagination';
import useMRFTranslation from "mrf/utils/useMRFTranslation";

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_MAX_COLUMNS = 34;

const queryCallback = (obj, queryString, queryableFields) => {
    const terms = queryString.split(" ").filter(x => x)
    const fieldValues = queryableFields.map(field => obj[field]?.toLowerCase?.() ?? "")
    
    for (let term of terms) {
        let found = false
        for (let fieldValue of fieldValues) {
            if (fieldValue.includes(term)) {
                found = true
                break
            } 
        }
        if (!found) {
            return false
        }
    }

    return true
}

const renderNestedTableCell = ({ resources, idx, field, fieldKey, obj, fieldDescriptor, i = 0 }) => {
    const resource = resources?.[fieldDescriptor?.resource]?.byId?.[obj];

    if (field.keys && i + 1 < field.keys.length) {
        fieldKey = field.keys?.[++i];
        obj = resource?.[fieldKey] ?? resource?.[fieldKey + 'Id'] ?? obj?.[fieldKey];

        return renderNestedTableCell({
            resources,
            idx,
            field,
            fieldKey,
            obj,
            fieldDescriptor: fieldDescriptor?.descriptor?.[fieldKey],
            i: i + 1,
        });
    }

    const data = fieldDescriptor?.hasOwnProperty(fieldKey)
        ? resource
        : obj;

    return (
        field.map
            ? field.map(data, idx)
            : data
    ) ?? '';
}

export const renderTableCell = ({ t, resources, idx, obj, field, descriptor }) => {
    if (!obj) {
        return '';
    }

    if (field.keys) {
        const fieldKey = field.keys?.[0];
        return renderNestedTableCell({
            resources,
            idx,
            field,
            fieldKey,
            obj: obj[fieldKey] ?? obj[fieldKey + 'Id'],
            fieldDescriptor: descriptor?.[fieldKey],
        });
    }

    if (field.key) {
        if (descriptor?.hasOwnProperty(field.key)) {
            return renderNestedTableCell({
                resources,
                idx,
                field,
                fieldKey: field.key,
                obj: obj[field.key] ?? obj[field.key + 'Id'],
                fieldDescriptor: descriptor?.[field.key],
            });
        }

        let fieldNestedValue = obj[field.key];

        if (fieldNestedValue && field.translate) {
            fieldNestedValue = t(fieldNestedValue);
        }

        return (field.map
            ? field.map(fieldNestedValue)
            : fieldNestedValue) ?? '';
    }

    if (field.map) {
        return field.map(obj);
    }
}

const sortResource = ({ t, dispatch, resource, fields, sortBy, isAsc }) => {
    const { resources } = store.getState();
    const field = { ...fields[sortBy], map: undefined };

    const list = resources[resource].list
        .map(idx => resources[resource].byId[idx]) // Get full object by its id
        .map(obj => // Add the value to sort in a tuple
            [
                obj,
                renderTableCell({
                    t,
                    resources,
                    idx: sortBy,
                    obj,
                    field,
                })
            ]
        )
        //Turn array in comma separated list
        .map(([obj, field]) => [obj, Array.isArray(field) ? field.map(f => t(f)).toString() : field])

        // eslint-disable-next-line no-unused-vars
        .sort(([_1, a], [_2, b]) => { // Sort by the second element of the tuple
            // Sort Numbers
            if (!isNaN(a) && !isNaN(b)) {
                return isAsc ? a - b : b - a;
            } else if (!isNaN(a)) {
                return -1;
            } else if (!isNaN(b)) {
                return 1;
            }

            const dateA = new Date(a);
            const dateB = new Date(b)
            // Sort Dates
            if (!isNaN(dateA) && !isNaN(dateB)) {
                // `1 - 2x` maps `true` to -1 and `false` to 1
                return 1 - 2 * (isAsc ? dateA < dateB : dateB < dateA);
            } else if (!isNaN(dateA)) {
                return -1;
            } else if (!isNaN(dateB)) {
                return 1;
            }

            // Sort Strings
            if (a && b) {
                return isAsc ? a.localeCompare(b) : -a.localeCompare(b);
            } else if (a) {
                return -1;
            } else if (b) {
                return 1;
            }

            return 0;
        })
        .map(([obj]) => obj); // Discart the second element of the tuple

    dispatch(addResourceAction(resource, list));
}

FetcherObjectTable.propTypes = {
    endpoint: PropTypes.string, // Query endpoint.
    resource: PropTypes.string, // Resource name that data will be stored in redux
    onclick: PropTypes.func, // Callback function to handle clicks on table
};

export default function FetcherObjectTable({
    endpoint,
    resource,
    dependencyList = [],
    onClick,
    loadingMessage = 'Loading...',
    errorMessage = 'Unexpected error occurred...',
    fields,
    csvTitle,
    csvUser,
    queryableFields,
    usePages,
    showCsvButton,
    ...props
}) {
    const dispatch = useDispatch();
    const t = useMRFTranslation();

    const [messageOverride, setMessageOverride] = useState(loadingMessage);
    const { list, byId } = useSelector(state => state.resources[resource]) ?? {};

    const context = useFormSelector({
        resource: resource + 'QueryAndSorting',
        blankForm: () => ({
            page: 0,
            pageSize: DEFAULT_PAGE_SIZE,
            pageCount: -1,
            rowCount: -1,
        }),
        blankCondition: () => true,
    });
    const { data: { sortBy, sortDir, ...data } = {}, set } = useFormFullSelector(context);

    useEffect(() => {
        fetchTable();
    }, dependencyList); // eslint-disable-line

    useEffect(() => {
        const isAsc = sortDir === 'asc';
        if (sortBy >= 0) {
            sortResource({ t, dispatch, resource, fields, sortBy, isAsc });
        }
    }, [t, resource, fields, sortBy, sortDir]); // eslint-disable-line

    const fetchTable = async () => {
        setMessageOverride(loadingMessage);

        try {
            await resourceGetList({ dispatch, resource, endpoint });
            setMessageOverride(undefined);
        } catch (e) {
            setMessageOverride(errorMessage);
            throw e;
        }
    }

    const handleSort = idx => {
        set({
            sortBy: idx,
            sortDir: idx !== sortBy || sortDir === 'desc'
                ? 'asc'
                : 'desc'
        });
    }

    const limitedFields = useMemo(
        () => {
            if(fields.length < DEFAULT_MAX_COLUMNS)
                return fields;
            return [
                {name: 'Information', key: 'id'},
            ]
        },
        [fields]
    )

    const filteredTermList = useMemo(
        () => {
            if (fields.length >= DEFAULT_MAX_COLUMNS){
                return []
            }
            if (!queryableFields) {
                return list ?? []
            }
            const queryString = data?.queryString?.toLowerCase?.() ?? ''
            return (list ?? []).filter(id => queryCallback(byId[id], queryString, queryableFields))
        },
        [byId, list, data?.queryString, queryableFields, fields ]
    )

    const pagedList = useMemo(
        () => {
            if (!usePages) {
                return filteredTermList
            }

            const page = data?.page ?? 0;
            const pageSize = data?.pageSize ?? DEFAULT_PAGE_SIZE;

            return filteredTermList.slice(page*pageSize, page*pageSize + (+pageSize))
        },
        [filteredTermList, data?.page, data?.pageSize, usePages]
    )

    const customEmptyListLabel = useMemo(
        () => {
            if(fields.length < DEFAULT_MAX_COLUMNS)
                return null;
            return "This table is too big to be displayed, you must export it to view it."
        },
        [fields]
    )

    const doSearch = () => {
        const pageCount = Math.ceil((filteredTermList?.length ?? 0) / (data?.pageSize ?? DEFAULT_PAGE_SIZE));
        let page = (data?.page ?? 0) >= pageCount ? pageCount-1 : (data?.page ?? 0);
        if (page < 0) {
            page = 0
        }

        set({
            page,
            pageCount,
            rowCount: (filteredTermList?.length ?? 0),
        });
    }

    // Handle page size change and page change.
    useEffect(doSearch, [data?.page, data?.pageSize, filteredTermList, set]);

    return <Flex direction='col'>
        <IF condition={!!queryableFields}>
            <SearchField
                placeholder='Search'
                field='queryString'
                context={context}
                handleSearch={doSearch}
                id={generateHtmlId(resource, 'search')}
            />
        </IF>
        <ObjectTable
            htmlId={generateHtmlId('table', resource)}
            footer
            onClick={onClick}
            messageOverride={messageOverride}
            fields={limitedFields}
            onSort={handleSort}
            sortedByIdx={sortBy}
            sortedByDir={sortDir}
            list={pagedList}
            byId={byId}
            emptyListLabel={customEmptyListLabel}
            {...props}
        />
        {usePages ? <>
            <Pagination context={context} fields={fields} list={list} byId={byId} showCsvButton={showCsvButton} />
        </> : <>
            <TableFooter
                fields={fields}
                csvTitle={csvTitle}
                csvUser={csvUser}
                list={list}
                byId={byId}
            />
        </>}
    </Flex>
}
